import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

// Define types for the medical plan structure
interface Medication {
  name: string;
  cost: number;
}

interface Consultation {
  name: string;
  cost: number;
}

interface Appointment {
  title: string;
  description: string;
  location: string;
  date: string;
  time: string;
  timezone: string;
  program: string;
  session_id: string | null;
  additionalProp1: Record<string, any>;
}

interface MedicalPlanVariables {
  program: string;
  session_id: string | null;
  medication: Medication;
  consultation: Consultation;
  appointment: Appointment;
  success_url: string;
  cancel_url: string;
}

interface MedicalPlanStore {
  medicalPlanVariables: MedicalPlanVariables;
  
  // Actions
  setMedicalPlanVariables: (variables: MedicalPlanVariables) => void;
  updateMedication: (medication: Partial<Medication>) => void;
  updateConsultation: (consultation: Partial<Consultation>) => void;
  updateAppointment: (appointment: Partial<Appointment>) => void;
  setSessionId: (sessionId: string | null) => void;
  setUrls: (successUrl: string, cancelUrl: string) => void;
  resetStore: () => void;
  
  // Helper to create variables from form data
  createFromFormData: (formData: {
    medication: Medication;
    consultationType: Consultation;
  }, successUrl: string, cancelUrl: string) => void;
}

// Default state
const defaultMedicalPlanVariables: MedicalPlanVariables = {
  program: "weight_loss",
  session_id: null,
  medication: {
    name: "",
    cost: 0,
  },
  consultation: {
    name: "",
    cost: 0,
  },
  appointment: {
    title: "Initial Consultation",
    description: "Medical consultation and plan setup",
    location: "Online",
    date: new Date().toISOString().split('T')[0]!,
    time: new Date().toTimeString().split(' ')[0]!,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    program: "weight_loss",
    session_id: null,
    additionalProp1: {},
  },
  success_url: "",
  cancel_url: "",
};

// Create the Zustand store
export const useMedicalPlanStore = create<MedicalPlanStore>()(
  devtools(
    persist(
      (set) => ({
        medicalPlanVariables: defaultMedicalPlanVariables,

        setMedicalPlanVariables: (variables) =>
          set(
            { medicalPlanVariables: variables },
            false,
            'setMedicalPlanVariables'
          ),

        updateMedication: (medication) =>
          set(
            (state) => ({
              medicalPlanVariables: {
                ...state.medicalPlanVariables,
                medication: {
                  ...state.medicalPlanVariables.medication,
                  ...medication,
                },
              },
            }),
            false,
            'updateMedication'
          ),

        updateConsultation: (consultation) =>
          set(
            (state) => ({
              medicalPlanVariables: {
                ...state.medicalPlanVariables,
                consultation: {
                  ...state.medicalPlanVariables.consultation,
                  ...consultation,
                },
              },
            }),
            false,
            'updateConsultation'
          ),

        updateAppointment: (appointment) =>
          set(
            (state) => ({
              medicalPlanVariables: {
                ...state.medicalPlanVariables,
                appointment: {
                  ...state.medicalPlanVariables.appointment,
                  ...appointment,
                },
              },
            }),
            false,
            'updateAppointment'
          ),

        setSessionId: (sessionId) =>
          set(
            (state) => ({
              medicalPlanVariables: {
                ...state.medicalPlanVariables,
                session_id: sessionId,
                appointment: {
                  ...state.medicalPlanVariables.appointment,
                  session_id: sessionId,
                },
              },
            }),
            false,
            'setSessionId'
          ),

        setUrls: (successUrl, cancelUrl) =>
          set(
            (state) => ({
              medicalPlanVariables: {
                ...state.medicalPlanVariables,
                success_url: successUrl,
                cancel_url: cancelUrl,
              },
            }),
            false,
            'setUrls'
          ),

        resetStore: () =>
          set(
            { medicalPlanVariables: defaultMedicalPlanVariables },
            false,
            'resetStore'
          ),

        createFromFormData: (formData, successUrl, cancelUrl) => {
          const variables: MedicalPlanVariables = {
            program: "weight_loss",
            session_id: null,
            medication: {
              name: formData.medication.name,
              cost: formData.medication.cost,
            },
            consultation: {
              name: formData.consultationType.name,
              cost: formData.consultationType.cost,
            },
            appointment: {
              title: "Initial Consultation",
              description: "Medical consultation and plan setup",
              location: "Online",
              date: new Date().toISOString().split('T')[0]!,
              time: new Date().toTimeString().split(' ')[0]!,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              program: "weight_loss",
              session_id: null,
              additionalProp1: {},
            },
            success_url: successUrl,
            cancel_url: cancelUrl,
          };

          set(
            { medicalPlanVariables: variables },
            false,
            'createFromFormData'
          );
        },
      }),
      {
        name: 'medical-plan-storage',
        // You can customize which parts to persist
        partialize: (state) => ({ medicalPlanVariables: state.medicalPlanVariables }),
      }
    ),
    {
      name: 'medical-plan-store',
    }
  )
);

// Selector hooks for easier access to specific parts of the state
export const useMedicalPlanVariables = () => 
  useMedicalPlanStore((state) => state.medicalPlanVariables);

export const useMedication = () => 
  useMedicalPlanStore((state) => state.medicalPlanVariables.medication);

export const useConsultation = () => 
  useMedicalPlanStore((state) => state.medicalPlanVariables.consultation);

export const useAppointment = () => 
  useMedicalPlanStore((state) => state.medicalPlanVariables.appointment);

export const useSessionId = () => 
  useMedicalPlanStore((state) => state.medicalPlanVariables.session_id);